/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import { opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import {
  deletePackCategory,
  getPackCategories,
  updatePackCategoryStatus,
} from "services/packCategoryServices";
import { getPackCategoryTableColumns } from "utils/tableHelper/extraTableHelper";
// import useOpenClose from "hooks/useOpenClose";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import configData from "config/config.json";

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  editorConatiner: {
    height: "100%",
    width: "100%",
    display: "flex",
    flexFlow: "column nowrap",
    justifyContent: "start",
    alignItems: "center",
    gap: 10,
    marginBottom: "10px",
  },
  field: { width: "220px" },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
    handleDisable?: (id: any) => void;
    edit?: (data: any) => void;
  };
}

const RowActions = ({ rowId, data, actionHandlers }: RowActionsProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    handleDelete = () => {},
    handleDisable = () => {},
    edit = () => {},
  } = actionHandlers;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PACK_CATEGORY.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => edit(data)}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisable(data?._id)}>
          {data.isActive ? "Disable" : "Enable"}
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
        )}
      </Menu>
    </>
  );
};

const PackCategories = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [packCategoriesList, setPackCategoriesList] = useState([]); // Full unfiltered list
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  // const [categoryDetails, setCategoryDetails] = useState({
  //   name: "",
  // });
  // const { isOpen, extraData, open, close, updateExtra } = useOpenClose();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.PACK.UPDATE_ROLES
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getPackCategories();
      const mappedData = resp?.data?.map((el) => ({
        ...el,
        isActiveText: el?.isActive ? "YES" : "NO",
        rowDisabled: !el?.isActive,
      }));
      setPackCategoriesList(mappedData || []);
      setFilteredList(mappedData || []);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message);
      setLoading(false);
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data.name, id: data._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deletePackCategory(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onDisable = async (id) => {
    try {
      const resp = await updatePackCategoryStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onSearch = (value: any) => {
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const createPack = () => navigate(configData.routes.packCategory.create);

  const editPack = (data: any) => {
    navigate(configData.routes.packCategory.edit.replace(":id", data._id));
  };

  // const onChange = (e: any) => {
  //   const { name, value } = e.target;
  //   setCategoryDetails((state) => ({ ...state, [name]: value }));
  // };

  // Local search filter - runs whenever searchValue changes
  useEffect(() => {
    if (!searchValue || searchValue.trim() === "") {
      setFilteredList(packCategoriesList);
      return;
    }

    const searchTerm = searchValue.toLowerCase().trim();
    const filtered = packCategoriesList.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(searchTerm);
      const idMatch = item._id?.toLowerCase().includes(searchTerm);
      return nameMatch || idMatch;
    });

    setFilteredList(filtered);
    onChangePage(defaultPageNo); // Reset to first page when search changes
  }, [searchValue, packCategoriesList]);

  useEffect(() => {
    const initialColumns = getPackCategoryTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
  }, []);

  return (
    <ContentShell
      title="Pack Categories"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a pack"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.PLAYER.PACK.CREATE_ROLES) && (
            <CustomButton onClick={createPack}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          hidePagination={true}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          onColumnClick={onColumnClick}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleDisable: onDisable,
            edit: editPack,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
      {/* <ModalContainer openModel={isOpen} handleClose={close}>
        <ModalLayout isLargeContainer={false} hasPadding={true}>
          <Box className={styles.editorConatiner}>
            <Typography>
              {(extraData.type == "add" ? "Add" : "Edit") + " Pack Category"}
            </Typography>
            <Box className={styles.field}>
              <SimpleTextInput
                label={"Name"}
                name={"name"}
                onChange={onChange}
                value={categoryDetails.name}
              />
            </Box>
            <CustomButton onClick={onSave}>
              {extraData.type == "add" ? "Save" : "Update"}
            </CustomButton>
          </Box>
        </ModalLayout>
      </ModalContainer> */}
    </ContentShell>
  );
};

export default PackCategories;
